import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { RadioButton } from 'primereact/radiobutton';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function DailyAttendance() {
    const [attendanceData, setAttendanceData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [teacherType, setTeacherType] = useState('All');
    const [teacherId, setTeacherId] = useState('');
    const [attendanceStatus, setAttendanceStatus] = useState('1');
    const [teacherTypeList, setTeacherTypeList] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 200,
        page: 0
    });

    useEffect(() => {
        loadTeacherTypes();
        loadDailyAttendance();
    }, []);

    const loadTeacherTypes = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/teacher-types`);
            setTeacherTypeList(response.data);
        } catch (error) {
            console.error('Error loading teacher types:', error);
            setTeacherTypeList([
                { label: 'All', value: 'All' },
                { label: '6-11 Teacher', value: '6 -11 Teacher' },
                { label: 'A/L Teacher', value: 'A/L Teacher' },
                { label: 'Primary', value: 'Primary' }
            ]);
        }
    };

    const loadDailyAttendance = async () => {
        setLoading(true);
        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const response = await axios.get(`${API_BASE_URL}/attendance/daily`, {
                params: {
                    date: dateStr,
                    teacherType: teacherType === 'All' ? '' : teacherType,
                    teacherId: teacherId,
                    status: attendanceStatus,
                    first: lazyParams.first,
                    rows: lazyParams.rows
                }
            });
            setAttendanceData(response.data.data || []);
            setTotalRecords(response.data.totalRecords || 0);
        } catch (error) {
            console.error('Error loading daily attendance:', error);
            setAttendanceData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setLazyParams({ ...lazyParams, first: 0, page: 0 });
        loadDailyAttendance();
    };


    const exportToExcel = () => {
        const exportData = attendanceData.map((row) => ({
            "TID": row.tid,
            "Teacher Name": row.teacher_name,
            "Grades": row.grades,
            "In Time": row.in_time,
            "Out Time": row.out_time
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Daily Attendance");
        XLSX.writeFile(wb, `DailyAttendance_${selectedDate.toISOString().split("T")[0]}.xlsx`);
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        const dateStr = selectedDate.toISOString().split("T")[0];
        const statusText = attendanceStatus === '1' ? 'All' : attendanceStatus === '2' ? 'Present' : 'Absent';
        const typeText = teacherType === 'All' ? 'All Types' : teacherType;
        
        // Title - changes based on status filter
        const reportTitle = attendanceStatus === '3' 
            ? "ANANDA COLLEGE - DAILY ABSENT REPORT" 
            : "ANANDA COLLEGE - DAILY ATTENDANCE REPORT";
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(reportTitle, 105, 15, { align: 'center' });
        
        // Search criteria summary
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(80);
        doc.text(`Date: ${dateStr}`, 14, 25);
        doc.text(`Teacher Type: ${typeText}`, 14, 30);
        doc.text(`Status: ${statusText}`, 80, 30);
        if (teacherId) {
            doc.text(`Sign ID (TID) or NIC: ${teacherId}`, 130, 30);
        }
        doc.text(`Total Records: ${totalRecords}`, 14, 35);
        
        // Reset text color
        doc.setTextColor(0);
        
        const tableData = attendanceData.map((row, index) => [
            index + 1,
            row.tid,
            row.teacher_name,
            row.grades || '-',
            row.in_time || '-',
            row.out_time || '-'
        ]);
        
        autoTable(doc, {
            head: [["#", "TID", "Teacher Name", "Grades", "In Time", "Out Time"]],
            body: tableData,
            startY: 42,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [30, 58, 95], textColor: 255 },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            columnStyles: {
                0: { cellWidth: 10 },
                1: { cellWidth: 15 },
                2: { cellWidth: 55 },
                3: { cellWidth: 40 },
                4: { cellWidth: 25 },
                5: { cellWidth: 25 }
            }
        });
        
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(128);
            doc.text(`Generated: ${new Date().toLocaleString()}`, 14, doc.internal.pageSize.height - 10);
            doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
        }
        
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    };
    const onPage = (event) => {
        setLazyParams(event);
    };

    useEffect(() => {
        loadDailyAttendance();
    }, [lazyParams.first, lazyParams.rows]);

    const formattedDate = selectedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const rowIndexTemplate = (rowData, options) => {
        const first = lazyParams?.first || 0;
        return first + (options?.rowIndex || 0) + 1;
    };

    const lastPunchDateTemplate = (rowData) => {
        if (!rowData.lastPunchDate) return '-';
        const d = new Date(rowData.lastPunchDate);
        if (Number.isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    return (
        <div className="card p-fluid">
            {loading && (
                <div className="loading-overlay">
                    <ProgressSpinner />
                </div>
            )}

            <div style={{ textAlign: 'center' }}>
                <h1 className="page-title">
                    TEACHERS DAILY ATTENDANCE OVERVIEW REPORT OF ANANDA COLLEGE {formattedDate}
                </h1>

                <Panel className="search-panel" style={{ marginBottom: '1rem' }}>
                    <div className="filter-row">
                        <div className="filter-item">
                            <label htmlFor="date">Date</label>
                            <Calendar
                                id="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.value)}
                                dateFormat="yy-mm-dd"
                                showIcon
                            />
                        </div>

                        <div className="filter-item">
                            <label htmlFor="type">Teacher Type</label>
                            <Dropdown
                                id="type"
                                value={teacherType}
                                options={teacherTypeList}
                                optionLabel="label"
                                optionValue="value"
                                onChange={(e) => setTeacherType(e.value)}
                                placeholder="Select Teacher Type"
                            />
                        </div>

                        <div className="filter-item">
                            <label htmlFor="teacherId">Sign ID (TID) or NIC</label>
                            <InputText
                                id="teacherId"
                                value={teacherId}
                                onChange={(e) => setTeacherId(e.target.value)}
                                placeholder="Enter Sign ID (TID) or NIC"
                            />
                        </div>

                        <div className="filter-item">
                            <label>Status</label>
                            <div className="status-radio-group">
                                <div className="radio-item">
                                    <RadioButton
                                        inputId="statusAll"
                                        name="status"
                                        value="1"
                                        checked={attendanceStatus === '1'}
                                        onChange={(e) => setAttendanceStatus(e.value)}
                                    />
                                    <label htmlFor="statusAll">All</label>
                                </div>
                                <div className="radio-item">
                                    <RadioButton
                                        inputId="statusAttend"
                                        name="status"
                                        value="2"
                                        checked={attendanceStatus === '2'}
                                        onChange={(e) => setAttendanceStatus(e.value)}
                                    />
                                    <label htmlFor="statusAttend">Attend</label>
                                </div>
                                <div className="radio-item">
                                    <RadioButton
                                        inputId="statusAbsent"
                                        name="status"
                                        value="3"
                                        checked={attendanceStatus === '3'}
                                        onChange={(e) => setAttendanceStatus(e.value)}
                                    />
                                    <label htmlFor="statusAbsent">Absent</label>
                                </div>
                            </div>
                        </div>
                    
                    <div className="filter-item filter-button">
                        <Button
                            label="Search"
                            icon="pi pi-search"
                            onClick={handleSearch}
                            className="p-button-success"
                            style={{ marginTop: "auto" }}
                        />
                    </div>
                    </div>
                </Panel>

                <Panel className="data-panel">
                    <DataTable
                        value={attendanceData}
                        lazy
                        paginator
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        rowsPerPageOptions={[200, 400, 600, 800]}
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        loading={loading}
                        scrollable
                        scrollHeight="calc(100vh - 350px)"
                        stickyHeader
                        resizableColumns
                        header={
                            <div style={{ float: 'right' }}>
                                <Button
                                    onClick={exportToExcel} icon="pi pi-file-excel"
                                    className="p-button-success p-button-text export-button"
                                    tooltip="Export to Excel"
                                    style={{ marginRight: '5px' }}
                                />
                                <Button
                                    onClick={exportToPDF} icon="pi pi-file-pdf"
                                    className="p-button-danger p-button-text export-button"
                                    tooltip="Export to PDF"
                                />
                            </div>
                        }
                    >
                        <Column
                            header="#"
                            body={rowIndexTemplate}
                            frozen
                            alignFrozen="left"
                            style={{ width: '60px', textAlign: 'center', backgroundColor: '#fff' }}
                        />
                        <Column
                            field="tid"
                            header="Teacher Sign ID"
                            sortable
                            style={{ textAlign: 'center', width: '60px' }}
                        />
                        <Column
                            field="teacher_name"
                            header="Teacher Name"
                            style={{ textAlign: 'left', minWidth: '200px' }}
                        />
                        <Column
                            field="grades"
                            header="Grades"
                            style={{ textAlign: 'center', width: '100px' }}
                        />
                        <Column
                            field="in_time"
                            header="In Time"
                            sortable
                            style={{ textAlign: 'center', width: '100px' }}
                        />
                        <Column
                            field="out_time"
                            header="Out Time"
                            style={{ textAlign: 'center', width: '100px' }}
                        />
                        <Column
                            field="lastPunchDate"
                            header="Last Punch Date"
                            body={lastPunchDateTemplate}
                            style={{ textAlign: 'center', width: '130px' }}
                        />
                    </DataTable>
                </Panel>
            </div>
        </div>
    );
}

export default DailyAttendance;
